/**
 * Engine: structural mutations.
 *
 * Pure, immutable rebuilds for the four primitive tree edits the UI surfaces:
 * insertChild, insertSibling, updateNode, removeNode. Every operation returns a
 * fresh tree array (never mutates input) so React 19 reconciliation sees a new
 * reference. Recursion only descends into a node's children when the target key
 * was not matched at the current level, which keeps the walk shallow.
 * `updateNode` is typed by tree-type discriminant: callers must pass an
 * update shape compatible with the matched node's `treeType`, so
 * `recipientConfig` cannot leak into non-recipient nodes.
 */
import type {
  RelationshipTreeContactNode,
  RelationshipTreeNode,
} from '../System.RelationshipTree.types';

export const insertChild = (
  trees: RelationshipTreeNode[],
  parentKey: string,
  newNode: RelationshipTreeNode,
): RelationshipTreeNode[] =>
  trees.map((node) => {
    if (node.key === parentKey) {
      return { ...node, children: [...(node.children ?? []), newNode] };
    }
    if (node.children) {
      return { ...node, children: insertChild(node.children, parentKey, newNode) };
    }
    return node;
  });

export const insertSibling = (
  trees: RelationshipTreeNode[],
  siblingKey: string,
  newNode: RelationshipTreeNode,
): RelationshipTreeNode[] => {
  const siblingIndex = trees.findIndex((node) => node.key === siblingKey);
  if (siblingIndex !== -1) {
    const next = [...trees];
    next.splice(siblingIndex + 1, 0, newNode);
    return next;
  }
  return trees.map((node) => {
    if (node.children) {
      return { ...node, children: insertSibling(node.children, siblingKey, newNode) };
    }
    return node;
  });
};

type NodeUpdate =
  | (Partial<Omit<RelationshipTreeContactNode, 'treeType' | 'key'>> & { treeType?: 'recipient' })
  | (Partial<Omit<RelationshipTreeNode, 'treeType' | 'key' | 'recipientConfig'>> & {
      recipientConfig?: never;
    });

const mergeNode = (
  node: RelationshipTreeNode,
  updates: NodeUpdate,
): RelationshipTreeNode => {
  if (node.treeType === 'recipient') {
    return { ...node, ...updates } as RelationshipTreeContactNode;
  }
  const { recipientConfig: _ignored, ...rest } = updates as NodeUpdate & {
    recipientConfig?: unknown;
  };
  return { ...node, ...rest } as RelationshipTreeNode;
};

export const updateNode = (
  trees: RelationshipTreeNode[],
  key: string,
  updates: NodeUpdate,
): RelationshipTreeNode[] =>
  trees.map((node) => {
    if (node.key === key) {
      return mergeNode(node, updates);
    }
    if (node.children) {
      return { ...node, children: updateNode(node.children, key, updates) };
    }
    return node;
  });

export const removeNode = (
  trees: RelationshipTreeNode[],
  key: string,
): RelationshipTreeNode[] => {
  const filtered = trees.filter((node) => node.key !== key);
  return filtered.map((node) => {
    if (node.children) {
      return { ...node, children: removeNode(node.children, key) };
    }
    return node;
  });
};
